export default `
# Shipment objects

These object types are used by the new shipment workflow: price validation, PDF upload and final shipment confirmation.

## ShipmentPriceDetails

Returned by \`GET /api/product/products/price\`. All price values are expressed in euro cents, except \`vat\`, which is a percentage.

\`\`\`json
{
  "shipmentPriceVat": 726,
  "shipmentPriceNoVat": 600,
  "priceFirstSheet": 120,
  "priceNextSheet": 35,
  "deliveryPrice": 480,
  "vat": 21
}
\`\`\`

| Field | Type | Description |
|-------|------|-------------|
| \`shipmentPriceVat\` | integer | Total shipment price including VAT, in cents. |
| \`shipmentPriceNoVat\` | integer | Total shipment price excluding VAT, in cents. |
| \`priceFirstSheet\` | integer | Price of the first printed sheet, in cents. |
| \`priceNextSheet\` | integer | Price of each additional printed sheet, in cents. |
| \`deliveryPrice\` | integer | Fixed delivery price, in cents. |
| \`vat\` | integer | VAT percentage applied to the net price. |

## DraftInfo

Returned by \`POST /api/outbox/drafts\` after uploading one or more PDF files.

\`\`\`json
{
  "draftId": "<draft identifier>",
  "documentName": "contract.pdf",
  "description": "",
  "uploadTime": "2026-07-01T10:15:00Z",
  "pages": 5,
  "ownerUser": "juan@acme.com",
  "timesUsed": 0
}
\`\`\`

| Field | Type | Description |
|-------|------|-------------|
| \`draftId\` | string | Unique identifier of the uploaded PDF document. Use it in \`Shipment.pages\`. |
| \`documentName\` | string | File name stored by PostaMail. |
| \`description\` | string | Optional document description. |
| \`uploadTime\` | datetime | Upload timestamp. |
| \`pages\` | integer | Number of pages in the PDF. |
| \`ownerUser\` | string | Email of the user that uploaded the document. |
| \`timesUsed\` | integer | Number of times the draft has been used in shipments. |

## Shipment

Sent to \`PUT /api/outbox/shipments\` to create/update a shipment. The response is the same object enriched with \`shipmentId\` and \`shipmentCode\`.

The \`Shipment\` object contains the following nested objects:

| Field | Type | Description |
|-------|------|-------------|
| \`pages\` | [ShipmentPages](/docs/api/shipment-objects#shipmentpages)[] | Array of ShipmentPages objects, one per page range from an uploaded draft. |
| \`destinationAddress\` | [ShipmentAddress](/docs/api/shipment-objects#shipmentaddress) | Destination address: one ShipmentAddress object. |
| \`senderAddress\` | [ShipmentAddress](/docs/api/shipment-objects#shipmentaddress) | Sender address: a second ShipmentAddress object. |
| \`features\` | [ShipmentFeatures](/docs/api/shipment-objects#shipmentfeatures) | One ShipmentFeatures object with the product, print options, reference and accepted prices. |

\`\`\`json
{
  "shipmentId": null,
  "shipmentCode": null,
  "emissionTime": "2026-07-01T10:30:00Z",
  "cancelTime": null,
  "pages": [
    {
      "draftId": "<draft identifier>",
      "pageFirst": 1,
      "pageLast": 5
    }
  ],
  "destinationAddress": {
    "who": "Juan Perez",
    "contact": "Departamento Legal",
    "phone": "+34910000000",
    "identityCard": "00000000T",
    "countryIso": "ES",
    "locality": "Madrid",
    "postalCode": "28001",
    "streetType": "Calle",
    "streetName": "Mayor",
    "streetNumber": "1",
    "streetComplements": "2 A",
    "envelopeBox": null
  },
  "senderAddress": {
    "who": "ACME Legal",
    "identityCard": "B00000000",
    "countryIso": "ES",
    "locality": "Madrid",
    "postalCode": "28002",
    "streetType": "Calle",
    "streetName": "Origen",
    "streetNumber": "10"
  },
  "features": {
    "productCode": "BCC",
    "printColor": false,
    "printDuplex": false,
    "reference": "ACME-2026-0001",
    "price": 726,
    "priceFirstSheet": 120,
    "priceNextSheet": 35,
    "deliveryPrice": 480,
    "vat": 21
  }
}
\`\`\`

## ShipmentPages

Pages from uploaded draft documents that make up the shipment.

### Example

\`\`\`json
{
  "draftId": "<draft identifier>",
  "pageFirst": 1,
  "pageLast": 5
}
\`\`\`

| Field | Type | Description |
|-------|------|-------------|
| \`draftId\` | string | Identifier returned by \`POST /api/outbox/drafts\`. |
| \`pageFirst\` | integer | First PDF page to include, starting at 1. |
| \`pageLast\` | integer | Last PDF page to include. |

## ShipmentAddress

Used for both \`destinationAddress\` and \`senderAddress\`. The sender \`identityCard\` is required and must follow the applicable NIF/DNI rules.

### Example

\`\`\`json
{
  "who": "Juan Perez",
  "contact": "Departamento Legal",
  "phone": "+34910000000",
  "identityCard": "00000000T",
  "countryIso": "ES",
  "locality": "Madrid",
  "postalCode": "28001",
  "streetType": "Calle",
  "streetName": "Mayor",
  "streetNumber": "1",
  "streetComplements": "2 A",
  "envelopeBox": null
}
\`\`\`

| Field | Type | Description |
|-------|------|-------------|
| \`who\` | string | Recipient/sender name. |
| \`contact\` | string | Contact line, such as “to the attention of”. |
| \`phone\` | string | Contact phone. |
| \`identityCard\` | string | NIF/DNI or legal identifier. |
| \`countryIso\` | string | Destination country code in ISO alpha-2 format. |
| \`locality\` | string | City/locality. |
| \`postalCode\` | string | Postal code. |
| \`streetType\` | string | Street type. |
| \`streetName\` | string | Street name. |
| \`streetNumber\` | string | Street number. |
| \`streetComplements\` | string | Address complement. |
| \`envelopeBox\` | string[] | Optional envelope-window lines. |

## ShipmentFeatures

Options and accepted prices used to confirm the shipment.

### Example

\`\`\`json
{
  "productCode": "BCC",
  "printColor": false,
  "printDuplex": false,
  "reference": "ACME-2026-0001",
  "price": 726,
  "priceFirstSheet": 120,
  "priceNextSheet": 35,
  "deliveryPrice": 480,
  "vat": 21
}
\`\`\`

| Field | Type | Description |
|-------|------|-------------|
| \`productCode\` | string | Product code, such as \`BCC\` or \`BFX\`. See the products annex in the integration manual. |
| \`printColor\` | boolean | \`true\` for color printing, \`false\` for black and white. May be ignored depending on product type. |
| \`printDuplex\` | boolean | \`true\` for duplex printing, \`false\` for simplex. May be ignored depending on product type. |
| \`reference\` | string | Customer reference. Required for product \`NT2\` as the administrative case number. |
| \`price\` | integer | Accepted total price with VAT, from \`ShipmentPriceDetails.shipmentPriceVat\`. |
| \`priceFirstSheet\` | integer | Accepted first-sheet price, from \`ShipmentPriceDetails.priceFirstSheet\`. |
| \`priceNextSheet\` | integer | Accepted additional-sheet price, from \`ShipmentPriceDetails.priceNextSheet\`. |
| \`deliveryPrice\` | integer | Accepted delivery price, from \`ShipmentPriceDetails.deliveryPrice\`. |
| \`vat\` | integer | VAT percentage, from \`ShipmentPriceDetails.vat\`. |
`



// WEBPACK FOOTER //
// ./src/components/docs/api/content/shipmentObjects.js